import React, { useState, useMemo, useCallback, useRef, useEffect, useId } from 'react';
import { ButtonSimple, LoadingSpinner, IconNext } from '@momentum-ui/react-collaboration';
import { IAISummaryData } from './AISummary.types';
import { DislikeFeedbackDialog, IDislikeFeedbackResult } from './DislikeFeedbackDialog';
import { useTranslation } from 'react-i18next';

const LikeIcon: React.FC = () => <IconNext name="like" scale={14} />;
const DislikeIcon: React.FC = () => <IconNext name="dislike" scale={14} />;
const CheckIcon: React.FC = () => <IconNext name="check" scale={14} />;
const CopyIcon: React.FC = () => <IconNext name="copy" scale={14} />;

/** Parse HTML note into { intro, bullets } for rendering with inline styles */
function parseHtmlNote(html: string): { intro: string; bullets: string[] } {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const pElements = doc.querySelectorAll('p');
  const intro = pElements.length > 0 ? (pElements[0].textContent || '') : '';
  const bullets: string[] = [];
  doc.querySelectorAll('li').forEach((li) => {
    const text = li.textContent?.trim();
    if (text) bullets.push(text);
  });
  // Fallback: if no <li> found, return full text as intro
  if (bullets.length === 0 && !intro) {
    return { intro: doc.body.textContent || '', bullets: [] };
  }
  return { intro, bullets };
}

interface ICallRecapSummaryTabProps {
  summaryData: IAISummaryData | null;
  loading: boolean;
}

type Feedback = 'none' | 'liked' | 'disliked';

const TEXT2 = 'var(--mds-color-theme-text-secondary-normal)';
// Note: previous BORDER and HOVER_BG constants were removed when the column
// border-right and hover background were dropped per design. Hover state
// is still tracked (see useSectionHover) for copy-button + copied-state UX.

const VISUALLY_HIDDEN_STYLES: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const S = {
  root: {
    display: 'flex',
    flexDirection: 'row' as const,
    gap: 0,
    height: '100%',
  },
  notesCol: {
    flex: '3 1 0%',
    minWidth: 0,
    paddingRight: 16,
    overflowY: 'auto' as const,
    overflowX: 'hidden' as const,
  },
  actionsCol: {
    flex: '2 1 0%',
    minWidth: 0,
    paddingLeft: 16,
    overflowY: 'auto' as const,
    overflowX: 'hidden' as const,
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '0px',
    color: 'var(--mds-color-theme-text-primary-normal)',
    margin: '0 0 4px',
  },
  aiRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 10,
  },
  aiBadge: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '22px',
    letterSpacing: '0px',
    color: TEXT2,
  },
  iconBtn: {
    padding: 4,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    color: TEXT2,
  },
  iconBtnActive: {
    padding: 4,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    color: 'var(--mds-color-theme-text-accent-normal, #1170CF)',
  },
  notesText: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '0px',
    color: TEXT2,
    wordWrap: 'break-word' as const,
    overflowWrap: 'break-word' as const,
    margin: 0,
  },
  actionList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  actionItem: {
    padding: '4px 0',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '22px',
    letterSpacing: '0px',
    color: TEXT2,
    display: 'flex',
    gap: 6,
  },
  bullet: {
    flexShrink: 0,
    color: 'var(--mds-color-theme-text-primary-normal)',
  },
  center: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '2rem 1rem',
    color: TEXT2,
    fontSize: '0.8125rem',
  },
};

async function copyTextToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  }
}

/** Hook for hover + copy behavior on a section */
function useSectionHover(textToCopy: string, onCopySuccess: () => void) {
  const [hovered, setHovered] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  const handleCopy = useCallback(async () => {
    await copyTextToClipboard(textToCopy);
    setCopied(true);
    onCopySuccess();
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 5000);
  }, [textToCopy, onCopySuccess]);

  const onMouseEnter = useCallback(() => setHovered(true), []);
  const onMouseLeave = useCallback(() => { setHovered(false); setCopied(false); }, []);
  const onFocus = useCallback(() => setFocusWithin(true), []);
  const onBlur = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
      setFocusWithin(false);
    }
  }, []);

  return { active: hovered || focusWithin, copied, handleCopy, onMouseEnter, onMouseLeave, onFocus, onBlur };
}

const CopyButton: React.FC<{ label: string; title: string; onCopy: () => void; copied: boolean }> = ({
  label,
  title,
  onCopy,
  copied,
}) => {
  const [isHovering, setIsHovering] = React.useState(false);

  return (
    <div
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <ButtonSimple
        style={{
          background: isHovering ? 'var(--mds-color-theme-background-primary-hover, rgba(0,0,0,0.05))' : 'transparent',
          border: 'none',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '4px',
          borderRadius: '4px',
          color: copied ? 'var(--mds-color-theme-text-accent-normal, #1170CF)' : TEXT2,
          transition: 'background-color 0.15s ease, color 0.15s ease',
        }}
        onPress={onCopy}
        aria-label={label}
        title={title}
      >
        {copied ? <CheckIcon /> : <CopyIcon />}
      </ButtonSimple>
    </div>
  );
};

export const CallRecapSummaryTab: React.FC<ICallRecapSummaryTabProps> = ({
  summaryData,
  loading,
}) => {
  const { t } = useTranslation('WebexCallHistory');
  const [notesFeedback, setNotesFeedback] = useState<Feedback>('none');
  const [actionsFeedback, setActionsFeedback] = useState<Feedback>('none');
  const [feedbackDialog, setFeedbackDialog] = useState<'notes' | 'actions' | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const notesHeadingId = useId();
  const actionsHeadingId = useId();
  const copyToClipboardTitle = t('aiSummary.copyToClipboard', { defaultValue: 'Copy to clipboard' });
  const aiGeneratedLabel = t('aiSummary.aiGenerated', { defaultValue: 'AI-generated' });
  const likeTitle = t('aiSummary.like', { defaultValue: 'Like' });
  const dislikeTitle = t('aiSummary.dislike', { defaultValue: 'Dislike' });

  const announce = useCallback((message: string) => {
    setAnnouncement('');
    window.setTimeout(() => setAnnouncement(message), 0);
  }, []);

  const notesHover = useSectionHover(
    useMemo(() => {
      const parts: string[] = [];
      const pn = summaryData?.note ? parseHtmlNote(summaryData.note) : { intro: '', bullets: [] };
      if (pn.intro) parts.push(pn.intro);
      pn.bullets.forEach((b) => parts.push(`• ${b}`));
      return parts.join('\n');
    }, [summaryData?.note]),
    () => announce(t('aiSummary.callNotesCopied', { defaultValue: 'Call notes copied to clipboard' }))
  );
  const actionsHover = useSectionHover(
    useMemo(() => {
      if (!summaryData?.actionItems) return '';
      return summaryData.actionItems
        .map((item) => `• ${item.editedContent || item.content}`)
        .join('\n');
    }, [summaryData?.actionItems]),
    () => announce(t('aiSummary.actionItemsCopied', { defaultValue: 'Action items copied to clipboard' }))
  );

  const toggleFeedback = (current: Feedback, type: 'liked' | 'disliked', setter: (v: Feedback) => void, section?: 'notes' | 'actions') => {
    if (type === 'disliked') {
      if (current === 'disliked') {
        setter('none');
      } else {
        setter('disliked');
        if (section) setFeedbackDialog(section);
      }
      return;
    }
    setter(current === type ? 'none' : type);
  };

  const handleFeedbackSend = useCallback((result: IDislikeFeedbackResult) => {
    // TODO: Send feedback to API with section context (feedbackDialog) when endpoint is available
    setFeedbackDialog(null);
  }, [feedbackDialog]);

  const handleFeedbackClose = useCallback(() => {
    setFeedbackDialog(null);
  }, []);

  const parsedNote = useMemo(
    () => (summaryData?.note ? parseHtmlNote(summaryData.note) : { intro: '', bullets: [] }),
    [summaryData?.note]
  );

  if (loading) {
    return (
      <div style={S.center} role="status" aria-live="polite">
        <LoadingSpinner scale={32} />
        <span>{t('aiSummary.loadingSummary', { defaultValue: 'Loading summary...' })}</span>
      </div>
    );
  }
  if (!summaryData || summaryData.status === 'error') {
    return (
      <div style={S.center} role="status" aria-live="polite">
        <span>{t('aiSummary.summaryNotAvailable', { defaultValue: 'Summary is not available' })}</span>
      </div>
    );
  }
  if (summaryData.status === 'unavailable') {
    return (
      <div style={S.center} role="status" aria-live="polite">
        <span>{t('aiSummary.summaryGenerating', { defaultValue: 'Summary is being generated...' })}</span>
      </div>
    );
  }

  return (
    <div style={S.root} data-testid="recap-summary-tab">
      <span style={VISUALLY_HIDDEN_STYLES} aria-live="polite" aria-atomic="true">
        {announcement}
      </span>
      {/* Left column: Call notes */}
      <div
        style={{ ...S.notesCol, background: 'transparent', borderRadius: 8 }}
        onMouseEnter={notesHover.onMouseEnter}
        onMouseLeave={notesHover.onMouseLeave}
        onFocus={notesHover.onFocus}
        onBlur={notesHover.onBlur}
        role="region"
        aria-labelledby={notesHeadingId}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 id={notesHeadingId} style={S.sectionTitle}>
            {t('aiSummary.callNotesHeading', { defaultValue: 'Call notes' })}
          </h2>
          <CopyButton
            label={t('aiSummary.copyCallNotes', { defaultValue: 'Copy call notes to clipboard' })}
            title={copyToClipboardTitle}
            onCopy={notesHover.handleCopy}
            copied={notesHover.copied}
          />
        </div>
        <div
          style={S.aiRow}
          role="group"
          aria-label={t('aiSummary.callNotesFeedback', { defaultValue: 'Call notes feedback' })}
        >
          <span style={S.aiBadge}>{aiGeneratedLabel}</span>
          <svg width="4" height="4" viewBox="0 0 4 4" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
            <circle cx="2" cy="2" r="2" fill="currentColor" />
          </svg>
          <ButtonSimple
            style={notesFeedback === 'liked' ? S.iconBtnActive : S.iconBtn}
            onPress={() => toggleFeedback(notesFeedback, 'liked', setNotesFeedback)}
            aria-label={t('aiSummary.likeCallNotes', { defaultValue: 'Like call notes' })}
            aria-pressed={notesFeedback === 'liked'}
            title={likeTitle}
          >
            <LikeIcon />
          </ButtonSimple>
          <ButtonSimple
            style={notesFeedback === 'disliked' ? S.iconBtnActive : S.iconBtn}
            onPress={() => toggleFeedback(notesFeedback, 'disliked', setNotesFeedback, 'notes')}
            aria-label={t('aiSummary.dislikeCallNotes', { defaultValue: 'Dislike call notes' })}
            aria-pressed={notesFeedback === 'disliked'}
            title={dislikeTitle}
          >
            <DislikeIcon />
          </ButtonSimple>
        </div>
        <div style={S.notesText}>
          {parsedNote.intro && <p style={{ margin: '0 0 8px' }}>{parsedNote.intro}</p>}
          {parsedNote.bullets.length > 0 && (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {parsedNote.bullets.map((b, i) => (
                <li key={i} style={{ ...S.actionItem }}>
                  <span style={S.bullet}>&#x2022;</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Right column: Action items */}
      {summaryData.actionItems.length > 0 && (
        <div
          style={{ ...S.actionsCol, background: 'transparent', borderRadius: 8 }}
          onMouseEnter={actionsHover.onMouseEnter}
          onMouseLeave={actionsHover.onMouseLeave}
          onFocus={actionsHover.onFocus}
          onBlur={actionsHover.onBlur}
          role="region"
          aria-labelledby={actionsHeadingId}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2 id={actionsHeadingId} style={S.sectionTitle}>
              {t('aiSummary.actionItemsHeading', { defaultValue: 'Action items' })}
            </h2>
            <CopyButton
              label={t('aiSummary.copyActionItems', { defaultValue: 'Copy action items to clipboard' })}
              title={copyToClipboardTitle}
              onCopy={actionsHover.handleCopy}
              copied={actionsHover.copied}
            />
          </div>
          <div
            style={S.aiRow}
            role="group"
            aria-label={t('aiSummary.actionItemsFeedback', { defaultValue: 'Action items feedback' })}
          >
            <span style={S.aiBadge}>{aiGeneratedLabel}</span>
            <svg width="4" height="4" viewBox="0 0 4 4" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
              <circle cx="2" cy="2" r="2" fill="currentColor" />
            </svg>
            <ButtonSimple
              style={actionsFeedback === 'liked' ? S.iconBtnActive : S.iconBtn}
              onPress={() => toggleFeedback(actionsFeedback, 'liked', setActionsFeedback)}
              aria-label={t('aiSummary.likeActionItems', { defaultValue: 'Like action items' })}
              aria-pressed={actionsFeedback === 'liked'}
              title={likeTitle}
            >
              <LikeIcon />
            </ButtonSimple>
            <ButtonSimple
              style={actionsFeedback === 'disliked' ? S.iconBtnActive : S.iconBtn}
              onPress={() => toggleFeedback(actionsFeedback, 'disliked', setActionsFeedback, 'actions')}
              aria-label={t('aiSummary.dislikeActionItems', { defaultValue: 'Dislike action items' })}
              aria-pressed={actionsFeedback === 'disliked'}
              title={dislikeTitle}
            >
              <DislikeIcon />
            </ButtonSimple>
          </div>
          <ul style={S.actionList}>
            {summaryData.actionItems.map((item) => (
              <li key={item.id} style={S.actionItem}>
                <span style={S.bullet}>&#x2022;</span>
                <span>{item.editedContent || item.content}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <DislikeFeedbackDialog
        isOpen={feedbackDialog !== null}
        onClose={handleFeedbackClose}
        onSend={handleFeedbackSend}
        usePortal={false}
      />
    </div>
  );
};
